import type { LiveAnnouncer } from '@angular/cdk/a11y';
import { Injectable, Injector, inject, signal } from '@angular/core';
import { environment } from '../../../../environments/environment';

export type ToastKind = 'success' | 'error' | 'info';

export interface Toast {
  id: number;
  kind: ToastKind;
  message: string;
}

/** Milliseconds a toast stays before it hides on its own. Errors stay longer so a reader has time to take them in. */
const DURATION: Readonly<Record<ToastKind, number>> = {
  success: 5000,
  info: 5000,
  error: 8000,
};

const MAX_TOASTS = 3;

/**
 * Shows toasts and announces them to screen readers (errors assertive). A message already showing is not added again.
 * The CDK announcer loads with the toast host or on the first toast, never at start-up: the global error handler
 * makes this service eager.
 */
@Injectable({ providedIn: 'root' })
export class ToastService {
  private readonly injector = inject(Injector);
  private announcer: Promise<LiveAnnouncer> | null = null;
  private readonly items = signal<readonly Toast[]>([]);
  private nextId = 1;

  readonly toasts = this.items.asReadonly();

  success(message: string): void {
    this.show('success', message);
  }

  error(message: string): void {
    this.show('error', message);
  }

  info(message: string): void {
    this.show('info', message);
  }

  dismiss(id: number): void {
    this.items.update((list) => list.filter((toast) => toast.id !== id));
  }

  private show(kind: ToastKind, message: string): void {
    if (this.items().some((existing) => existing.kind === kind && existing.message === message)) {
      return;
    }

    const toast: Toast = { id: this.nextId++, kind, message };

    // Replace, never mutate: the host is OnPush.
    this.items.update((list) => [...list, toast].slice(-MAX_TOASTS));
    this.announce(message, kind === 'error' ? 'assertive' : 'polite');

    setTimeout(() => this.dismiss(toast.id), DURATION[kind]);
  }

  /** Loads the announcer ahead of the first toast, so its live region exists before a message is written to it. */
  preloadAnnouncer(): Promise<LiveAnnouncer> {
    if (this.announcer === null) {
      const pending = import('./live-announcer').then((module) => this.injector.get(module.LiveAnnouncer));

      this.announcer = pending;

      // Clearing the cache lets a later toast call import() again; whether the browser re-fetches is up to it.
      pending.catch(() => {
        if (this.announcer === pending) {
          this.announcer = null;
        }
      });
    }

    return this.announcer;
  }

  private announce(message: string, politeness: 'assertive' | 'polite'): void {
    this.preloadAnnouncer()
      .then((announcer) => announcer.announce(message, politeness))
      .catch((error: unknown) => {
        // The toast still shows; only this announcement is lost.
        if (!environment.production) {
          // eslint-disable-next-line no-console
          console.error(error);
        }
      });
  }
}
